/**
 * Decisión pura de "¿este device necesita una animación de sincronización?".
 *
 * Vive suelta (sin React, sin store) porque la consultan tres caminos muy
 * distintos: el resync de reconexión, el desborde de la cola de animaciones y
 * los tests. Mantenerla aparte evita que ninguno arrastre al resto.
 */

import { haversine } from "../leaflet/animation/distance.js";
import {
  SYNC_ABSENCE_THRESHOLD_MS,
  SYNC_QUEUE_THRESHOLD,
  SYNC_DELTA_THRESHOLD_MS,
  SYNC_DISTANCE_THRESHOLD_M,
} from "../../config/syncAnimation.js";

/**
 * Decide whether a sync animation should be triggered for a single device.
 *
 * Pure function — no side effects, no React. The distance gate is mandatory:
 * if the device hasn't moved at least SYNC_DISTANCE_THRESHOLD_M, no trigger
 * fires regardless of other inputs. After the gate, the first matching
 * condition wins, evaluated in priority order: tab-away → queue-overflow →
 * timestamp-delta.
 *
 * @param {object} arg
 * @param {[number, number]} arg.from       last position the user saw  (lat, lng)
 * @param {[number, number]} arg.to         latest known position       (lat, lng)
 * @param {number|null} arg.fromTs          ms since epoch (or null)
 * @param {number|null} arg.toTs            ms since epoch (or null)
 * @param {number|null} arg.absenceMs       ms the tab was hidden, or null if N/A
 * @param {number} arg.queueLength          current pendingQueue length, or 0
 * @returns {{reason: string} | null}
 */
export function evaluateSyncTrigger({
  from,
  to,
  fromTs,
  toTs,
  absenceMs,
  queueLength,
}) {
  if (!Array.isArray(from) || !Array.isArray(to)) return null;
  if (!Number.isFinite(from[0]) || !Number.isFinite(from[1])) return null;
  if (!Number.isFinite(to[0]) || !Number.isFinite(to[1])) return null;

  const distance = haversine(from[0], from[1], to[0], to[1]);
  if (distance < SYNC_DISTANCE_THRESHOLD_M) return null;

  if (Number.isFinite(absenceMs) && absenceMs > SYNC_ABSENCE_THRESHOLD_MS) {
    return { reason: "tab-away" };
  }
  if (Number.isFinite(queueLength) && queueLength > SYNC_QUEUE_THRESHOLD) {
    return { reason: "queue-overflow" };
  }
  if (
    Number.isFinite(fromTs) &&
    Number.isFinite(toTs) &&
    toTs - fromTs > SYNC_DELTA_THRESHOLD_MS
  ) {
    return { reason: "timestamp-delta" };
  }
  return null;
}

export default evaluateSyncTrigger;
